import type { PipSnapConfig } from "./pip-config";
import { DEFAULT_PIP_SNAP_CONFIG } from "./pip-config";
import type { PipResizeConfig, PipResizeCorner } from "./pip-resize-config";
import type {
	PipBounds,
	PipDimensions,
	PipPosition,
	PipSnapInsets,
} from "./pip-snap";
import { resolvePipSnapPadding } from "./pip-snap";

export function clampPipScale(
	scale: number,
	minScale: number,
	maxScale: number,
): number {
	return Math.min(Math.max(scale, minScale), maxScale);
}

export function snapPipScale(scale: number, config: PipResizeConfig): number {
	const clamped = clampPipScale(scale, config.minScale, config.maxScale);
	let nearest = config.snapScales[0] ?? clamped;
	let nearestDistance = Math.abs(clamped - nearest);

	for (const target of config.snapScales) {
		const distance = Math.abs(clamped - target);
		if (distance < nearestDistance) {
			nearest = target;
			nearestDistance = distance;
		}
	}

	if (nearestDistance <= config.snapThresholdScale) {
		return clampPipScale(nearest, config.minScale, config.maxScale);
	}

	return clamped;
}

export function resolveNextCycledPipSnapScale(
	currentScale: number,
	config: PipResizeConfig,
): number {
	const snaps = [...config.snapScales].sort((left, right) => left - right);
	if (snaps.length === 0) {
		return clampPipScale(currentScale, config.minScale, config.maxScale);
	}

	const snappedCurrent = snapPipScale(currentScale, config);
	const currentIndex = snaps.findIndex((scale) => scale === snappedCurrent);
	if (currentIndex >= 0) {
		return snaps[(currentIndex + 1) % snaps.length] ?? snaps[0];
	}

	const nextLarger = snaps.find((scale) => scale > currentScale);
	return nextLarger ?? snaps[0];
}

export function scalePipDimensions(
	baseDimensions: PipDimensions,
	scale: number,
): PipDimensions {
	return {
		widthPx: Math.max(1, Math.round(baseDimensions.widthPx * scale)),
		heightPx: Math.max(1, Math.round(baseDimensions.heightPx * scale)),
	};
}

export function resolvePipResizeAnchorPosition(
	corner: PipResizeCorner,
	position: PipPosition,
	dimensions: PipDimensions,
): PipPosition {
	switch (corner) {
		case "top-left":
			return {
				x: position.x + dimensions.widthPx,
				y: position.y + dimensions.heightPx,
			};
		case "top-right":
			return { x: position.x, y: position.y + dimensions.heightPx };
		case "bottom-left":
			return { x: position.x + dimensions.widthPx, y: position.y };
		default:
			return position;
	}
}

export function resolvePipPositionForScale(params: {
	corner: PipResizeCorner;
	anchorPosition: PipPosition;
	baseDimensions: PipDimensions;
	scale: number;
}): PipPosition {
	const dimensions = scalePipDimensions(params.baseDimensions, params.scale);

	switch (params.corner) {
		case "top-left":
			return {
				x: params.anchorPosition.x - dimensions.widthPx,
				y: params.anchorPosition.y - dimensions.heightPx,
			};
		case "top-right":
			return {
				x: params.anchorPosition.x,
				y: params.anchorPosition.y - dimensions.heightPx,
			};
		case "bottom-left":
			return {
				x: params.anchorPosition.x - dimensions.widthPx,
				y: params.anchorPosition.y,
			};
		default:
			return params.anchorPosition;
	}
}

export function resolvePipScaleFromPointerDelta(params: {
	corner: PipResizeCorner;
	startScale: number;
	deltaX: number;
	deltaY: number;
	baseDimensions: PipDimensions;
	minScale: number;
	maxScale: number;
}): number {
	const diagonal =
		params.baseDimensions.widthPx + params.baseDimensions.heightPx;
	if (diagonal <= 0) {
		return params.startScale;
	}

	let projectedDelta = 0;
	switch (params.corner) {
		case "bottom-right":
			projectedDelta = params.deltaX + params.deltaY;
			break;
		case "top-left":
			projectedDelta = -(params.deltaX + params.deltaY);
			break;
		case "top-right":
			projectedDelta = params.deltaX - params.deltaY;
			break;
		default:
			projectedDelta = -params.deltaX + params.deltaY;
			break;
	}

	const scaleDelta = projectedDelta / diagonal;
	return clampPipScale(
		params.startScale + scaleDelta,
		params.minScale,
		params.maxScale,
	);
}

/** Aspect-locked scale so the dragged corner tracks the pointer in stage coordinates. */
export function resolvePipScaleFromPointerPosition(params: {
	corner: PipResizeCorner;
	anchorPosition: PipPosition;
	pointerPosition: PipPosition;
	baseDimensions: PipDimensions;
	minScale: number;
	maxScale: number;
}): number {
	const { corner, anchorPosition, pointerPosition, baseDimensions } = params;
	if (baseDimensions.widthPx <= 0 || baseDimensions.heightPx <= 0) {
		return params.minScale;
	}

	let widthPx = 0;
	let heightPx = 0;
	switch (corner) {
		case "bottom-right":
			widthPx = pointerPosition.x - anchorPosition.x;
			heightPx = pointerPosition.y - anchorPosition.y;
			break;
		case "top-left":
			widthPx = anchorPosition.x - pointerPosition.x;
			heightPx = anchorPosition.y - pointerPosition.y;
			break;
		case "top-right":
			widthPx = pointerPosition.x - anchorPosition.x;
			heightPx = anchorPosition.y - pointerPosition.y;
			break;
		default:
			widthPx = anchorPosition.x - pointerPosition.x;
			heightPx = pointerPosition.y - anchorPosition.y;
			break;
	}

	const scaleW = widthPx / baseDimensions.widthPx;
	const scaleH = heightPx / baseDimensions.heightPx;
	const dominantScale = Math.max(scaleW, scaleH);
	if (!Number.isFinite(dominantScale)) {
		return params.minScale;
	}

	return clampPipScale(dominantScale, params.minScale, params.maxScale);
}

export function resolvePipSnapCycleResizeLayout(params: {
	draggedCorner: PipResizeCorner;
	position: PipPosition;
	currentScale: number;
	baseDimensions: PipDimensions;
	bounds: PipBounds;
	padding: PipResizeBoundsPadding;
	config: PipResizeConfig;
}): PipBoundsAwareResizeLayout {
	const startDimensions = scalePipDimensions(
		params.baseDimensions,
		params.currentScale,
	);
	const pinState = resolvePipResizeEdgePinState({
		position: params.position,
		dimensions: startDimensions,
		bounds: params.bounds,
		padding: params.padding,
	});
	const fixedCorner = resolvePipResizeFixedCorner(
		params.draggedCorner,
		pinState,
	);
	const fixedPoint = isPipResizeCornerPinned(fixedCorner, pinState)
		? resolveSnappedPipCornerPoint(
				fixedCorner,
				params.position,
				startDimensions,
				params.bounds,
				params.padding,
				pinState,
			)
		: resolvePipCornerPoint(fixedCorner, params.position, startDimensions);
	const nextScale = resolveNextCycledPipSnapScale(
		params.currentScale,
		params.config,
	);
	const dimensions = scalePipDimensions(params.baseDimensions, nextScale);
	const position = resolvePipPositionFromFixedCorner(
		fixedCorner,
		fixedPoint,
		dimensions,
	);

	return {
		scale: nextScale,
		position,
		dimensions,
	};
}

export function resolvePipResizeCornerCursorClass(
	corner: PipResizeCorner,
): string {
	switch (corner) {
		case "top-left":
		case "bottom-right":
			return "cursor-nwse-resize";
		default:
			return "cursor-nesw-resize";
	}
}

export const PIP_RESIZE_BOUNDS_PIN_EPSILON_PX = 2;

export interface PipResizeBoundsPadding {
	top: number;
	right: number;
	bottom: number;
	left: number;
}

export interface PipResizeEdgePinState {
	left: boolean;
	right: boolean;
	top: boolean;
	bottom: boolean;
}

export interface PipBoundsAwareResizeLayout {
	scale: number;
	position: PipPosition;
	dimensions: PipDimensions;
}

export function resolvePipCornerPoint(
	corner: PipResizeCorner,
	position: PipPosition,
	dimensions: PipDimensions,
): PipPosition {
	switch (corner) {
		case "top-left":
			return { x: position.x, y: position.y };
		case "top-right":
			return {
				x: position.x + dimensions.widthPx,
				y: position.y,
			};
		case "bottom-left":
			return {
				x: position.x,
				y: position.y + dimensions.heightPx,
			};
		default:
			return {
				x: position.x + dimensions.widthPx,
				y: position.y + dimensions.heightPx,
			};
	}
}

export function resolveOppositePipResizeCorner(
	corner: PipResizeCorner,
): PipResizeCorner {
	switch (corner) {
		case "top-left":
			return "bottom-right";
		case "top-right":
			return "bottom-left";
		case "bottom-left":
			return "top-right";
		default:
			return "top-left";
	}
}

export function resolvePipResizeEdgePinState(params: {
	position: PipPosition;
	dimensions: PipDimensions;
	bounds: PipBounds;
	padding: PipResizeBoundsPadding;
	epsilonPx?: number;
}): PipResizeEdgePinState {
	const epsilon = params.epsilonPx ?? PIP_RESIZE_BOUNDS_PIN_EPSILON_PX;
	const { position, dimensions, bounds, padding } = params;

	return {
		left: position.x <= padding.left + epsilon,
		right:
			position.x + dimensions.widthPx >=
			bounds.width - padding.right - epsilon,
		top: position.y <= padding.top + epsilon,
		bottom:
			position.y + dimensions.heightPx >=
			bounds.height - padding.bottom - epsilon,
	};
}

export function isPipResizeCornerPinned(
	corner: PipResizeCorner,
	pins: PipResizeEdgePinState,
): boolean {
	switch (corner) {
		case "top-left":
			return pins.left || pins.top;
		case "top-right":
			return pins.right || pins.top;
		case "bottom-left":
			return pins.left || pins.bottom;
		default:
			return pins.right || pins.bottom;
	}
}

export function resolvePipResizeFixedCorner(
	draggedCorner: PipResizeCorner,
	pins: PipResizeEdgePinState,
): PipResizeCorner {
	if (isPipResizeCornerPinned(draggedCorner, pins)) {
		return draggedCorner;
	}
	return resolveOppositePipResizeCorner(draggedCorner);
}

export function resolveSnappedPipCornerPoint(
	corner: PipResizeCorner,
	position: PipPosition,
	dimensions: PipDimensions,
	bounds: PipBounds,
	padding: PipResizeBoundsPadding,
	pins: PipResizeEdgePinState,
): PipPosition {
	const point = resolvePipCornerPoint(corner, position, dimensions);

	if (pins.left && (corner === "top-left" || corner === "bottom-left")) {
		point.x = padding.left;
	}
	if (pins.right && (corner === "top-right" || corner === "bottom-right")) {
		point.x = bounds.width - padding.right;
	}
	if (pins.top && (corner === "top-left" || corner === "top-right")) {
		point.y = padding.top;
	}
	if (
		pins.bottom &&
		(corner === "bottom-left" || corner === "bottom-right")
	) {
		point.y = bounds.height - padding.bottom;
	}

	return point;
}

export function resolvePipPositionFromFixedCorner(
	fixedCorner: PipResizeCorner,
	fixedPoint: PipPosition,
	dimensions: PipDimensions,
): PipPosition {
	switch (fixedCorner) {
		case "top-left":
			return { x: fixedPoint.x, y: fixedPoint.y };
		case "top-right":
			return {
				x: fixedPoint.x - dimensions.widthPx,
				y: fixedPoint.y,
			};
		case "bottom-left":
			return {
				x: fixedPoint.x,
				y: fixedPoint.y - dimensions.heightPx,
			};
		default:
			return {
				x: fixedPoint.x - dimensions.widthPx,
				y: fixedPoint.y - dimensions.heightPx,
			};
	}
}

function isPointerExpandingThroughPinnedDraggedCorner(params: {
	draggedCorner: PipResizeCorner;
	fixedPoint: PipPosition;
	pointer: PipPosition;
}): boolean {
	const { draggedCorner, fixedPoint, pointer } = params;

	switch (draggedCorner) {
		case "top-right":
			return pointer.x > fixedPoint.x || pointer.y < fixedPoint.y;
		case "top-left":
			return pointer.x < fixedPoint.x || pointer.y < fixedPoint.y;
		case "bottom-right":
			return pointer.x > fixedPoint.x || pointer.y > fixedPoint.y;
		default:
			return pointer.x < fixedPoint.x || pointer.y > fixedPoint.y;
	}
}

function resolveRawDimensionsFromFixedCorner(params: {
	fixedCorner: PipResizeCorner;
	fixedPoint: PipPosition;
	pointer: PipPosition;
}): { widthPx: number; heightPx: number } {
	const { fixedCorner, fixedPoint, pointer } = params;

	switch (fixedCorner) {
		case "top-left":
			return {
				widthPx: pointer.x - fixedPoint.x,
				heightPx: pointer.y - fixedPoint.y,
			};
		case "top-right":
			return {
				widthPx: fixedPoint.x - pointer.x,
				heightPx: pointer.y - fixedPoint.y,
			};
		case "bottom-left":
			return {
				widthPx: pointer.x - fixedPoint.x,
				heightPx: fixedPoint.y - pointer.y,
			};
		default:
			return {
				widthPx: fixedPoint.x - pointer.x,
				heightPx: fixedPoint.y - pointer.y,
			};
	}
}

function resolveMaxScaleForBounds(params: {
	fixedCorner: PipResizeCorner;
	fixedPoint: PipPosition;
	baseDimensions: PipDimensions;
	bounds: PipBounds;
	padding: PipResizeBoundsPadding;
}): number {
	const { fixedCorner, fixedPoint, baseDimensions, bounds, padding } = params;
	if (baseDimensions.widthPx <= 0 || baseDimensions.heightPx <= 0) {
		return Number.POSITIVE_INFINITY;
	}

	let maxWidthPx = Number.POSITIVE_INFINITY;
	let maxHeightPx = Number.POSITIVE_INFINITY;

	switch (fixedCorner) {
		case "top-left":
			maxWidthPx = bounds.width - padding.right - fixedPoint.x;
			maxHeightPx = bounds.height - padding.bottom - fixedPoint.y;
			break;
		case "top-right":
			maxWidthPx = fixedPoint.x - padding.left;
			maxHeightPx = bounds.height - padding.bottom - fixedPoint.y;
			break;
		case "bottom-left":
			maxWidthPx = bounds.width - padding.right - fixedPoint.x;
			maxHeightPx = fixedPoint.y - padding.top;
			break;
		default:
			maxWidthPx = fixedPoint.x - padding.left;
			maxHeightPx = fixedPoint.y - padding.top;
			break;
	}

	const scaleW = maxWidthPx / baseDimensions.widthPx;
	const scaleH = maxHeightPx / baseDimensions.heightPx;
	return Math.max(0, Math.min(scaleW, scaleH));
}

export function resolvePipBoundsAwareResizeLayout(params: {
	draggedCorner: PipResizeCorner;
	fixedCorner: PipResizeCorner;
	fixedPoint: PipPosition;
	pointerPosition: PipPosition;
	startDimensions: PipDimensions;
	startScale: number;
	pinState: PipResizeEdgePinState;
	baseDimensions: PipDimensions;
	bounds: PipBounds;
	padding: PipResizeBoundsPadding;
	minScale: number;
	maxScale: number;
}): PipBoundsAwareResizeLayout {
	const {
		draggedCorner,
		fixedCorner,
		fixedPoint,
		pointerPosition,
		startDimensions,
		startScale,
		pinState,
		baseDimensions,
		bounds,
		padding,
		minScale,
		maxScale,
	} = params;

	const draggedCornerPinned = isPipResizeCornerPinned(
		draggedCorner,
		pinState,
	);
	if (
		draggedCornerPinned &&
		isPointerExpandingThroughPinnedDraggedCorner({
			draggedCorner,
			fixedPoint,
			pointer: pointerPosition,
		})
	) {
		return {
			scale: startScale,
			position: resolvePipPositionFromFixedCorner(
				fixedCorner,
				fixedPoint,
				startDimensions,
			),
			dimensions: startDimensions,
		};
	}

	const raw = resolveRawDimensionsFromFixedCorner({
		fixedCorner,
		fixedPoint,
		pointer: pointerPosition,
	});

	const dominantScale = Math.max(
		raw.widthPx / baseDimensions.widthPx,
		raw.heightPx / baseDimensions.heightPx,
	);
	const boundsMaxScale = resolveMaxScaleForBounds({
		fixedCorner,
		fixedPoint,
		baseDimensions,
		bounds,
		padding,
	});

	let resolvedScale = clampPipScale(dominantScale, minScale, maxScale);
	if (Number.isFinite(boundsMaxScale)) {
		resolvedScale = Math.min(resolvedScale, boundsMaxScale);
	}
	if (!Number.isFinite(resolvedScale) || resolvedScale <= 0) {
		resolvedScale = startScale;
	}

	const dimensions = scalePipDimensions(baseDimensions, resolvedScale);
	const position = resolvePipPositionFromFixedCorner(
		fixedCorner,
		fixedPoint,
		dimensions,
	);

	return {
		scale: resolvedScale,
		position,
		dimensions,
	};
}

export function resolvePipResizeBoundsPadding(
	snapConfig?: PipSnapConfig,
	snapInsets?: PipSnapInsets,
): PipResizeBoundsPadding {
	return resolvePipSnapPadding(
		snapConfig ?? DEFAULT_PIP_SNAP_CONFIG,
		snapInsets,
	);
}
